import { Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';

import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';
import { AlertEvent } from '~/common/models/alert.model';
import type { BuildAssetUrlFn } from '~/common/utils/asset-url.utils';

export const EMER_ALERT_EVENTS = [
  AlertEvent.AUTOMATIC_CRASH_RESPONSE,
  AlertEvent.LOW_LEVEL_COLLISION,
  AlertEvent.THERMAL_EVENT,
  AlertEvent.THEFT_ALARM,
] as const;

export type EmerAlertEvent = (typeof EMER_ALERT_EVENTS)[number];

export const EMER_MESSAGE_KEY_BY_EVENT: Record<EmerAlertEvent, string> = {
  [AlertEvent.AUTOMATIC_CRASH_RESPONSE]:
    'settings:notifications.messages.automatic-crash-response',
  [AlertEvent.LOW_LEVEL_COLLISION]:
    'settings:notifications.messages.low-level-collision',
  [AlertEvent.THERMAL_EVENT]: 'settings:notifications.messages.thermal-event',
  [AlertEvent.THEFT_ALARM]: 'settings:notifications.messages.theft-alarm',
};

export const EMER_EVENT_NAME_KEY_BY_EVENT: Record<EmerAlertEvent, string> = {
  [AlertEvent.AUTOMATIC_CRASH_RESPONSE]:
    'settings:notification.card.name.automaticCrashResponse',
  [AlertEvent.LOW_LEVEL_COLLISION]:
    'settings:notification.card.name.lowLevelCollision',
  [AlertEvent.THERMAL_EVENT]: 'settings:notification.card.name.thermalEvent',
  [AlertEvent.THEFT_ALARM]: 'settings:notification.card.name.theftAlarm',
};

export type EmerAlertSubject = {
  label: string;
  href: string;
};

export type EmerUrlBuilders = {
  buildAssetUrl: BuildAssetUrlFn;
  buildDriverUrl: (driverId: string) => string;
};

/**
 * Checks whether an alert event type belongs to the EMER family.
 * @param event - Alert event type, e.g. `automatic-crash-response`
 * @returns True for the four EMER event types
 */
export const isEmerAlertEvent = (event?: string): event is EmerAlertEvent =>
  EMER_ALERT_EVENTS.some((emerEvent) => emerEvent === event);

type EventCarrier = { event?: { event?: string } };

/**
 * Checks whether an alert record, single or grouped, carries an EMER event.
 * @param record - Alert record from the alerts feed, or an alert held in the store
 * @returns True when the record's event type is an EMER event
 */
export const isEmerAlertRecord = (
  record?: EventCarrier & { lastGroupEvent?: EventCarrier }
): boolean =>
  isEmerAlertEvent(
    record?.event?.event ?? record?.lastGroupEvent?.event?.event
  );

/**
 * Resolves the name shown next to an EMER alert title and where it links.
 * The label prefers the driver name, then the vehicle name, then the VIN.
 * The driver name links to the driver when a driver id is present; every
 * other label links to the vehicle.
 * @param contentVars - The alert's content variable map
 * @param urlBuilders - Builders for vehicle and driver detail URLs
 * @returns The subject label and its link target
 */
export const getEmerAlertSubject = (
  contentVars: Record<string, string>,
  { buildAssetUrl, buildDriverUrl }: EmerUrlBuilders
): EmerAlertSubject => {
  const {
    driver_full_name: driverName,
    driver_id: driverId,
    alert_object_alias: vehicleName,
    alert_objectid: vin,
  } = contentVars;

  const vehicleHref = buildAssetUrl(vin || '');

  if (driverName) {
    return {
      label: driverName,
      href: driverId ? buildDriverUrl(driverId) : vehicleHref,
    };
  }

  return {
    label: vehicleName || vin || DEFAULT_EMPTY_VALUE,
    href: vehicleHref,
  };
};

/**
 * Builds the notification message for an EMER alert: the event title followed
 * by a link to the driver or vehicle.
 * @param event - EMER alert event type
 * @param contentVars - The alert's content variable map
 * @param urlBuilders - Builders for vehicle and driver detail URLs
 * @param onClick - Called when the subject link is clicked
 * @returns Translated message element
 */
export const getEmerEventMessage = (
  event: EmerAlertEvent,
  contentVars: Record<string, string>,
  urlBuilders: EmerUrlBuilders,
  onClick?: () => void
): JSX.Element => {
  const { label, href } = getEmerAlertSubject(contentVars, urlBuilders);

  return (
    <Trans
      i18nKey={EMER_MESSAGE_KEY_BY_EVENT[event]}
      components={{
        alink: (
          <Typography
            component={RouteLink}
            variant="body_medium"
            to={href}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          />
        ),
      }}
      values={{ subject: label }}
      parent="span"
      data-heap-redact-text
    />
  );
};
